/**
 * vehicle-selector.js
 *
 * Loads vehicle data from the Storefront API (paginated) to bypass Shopify
 * Liquid's 250-item list-metafield cap and the facet API value limit.
 *
 * Confirmed filter value formats (from live URL inspection):
 *
 *  filter.p.m.sga.vehicle_brand      → GID of sga-nest-vehicle_brand metaobject
 *                                       e.g. gid://shopify/Metaobject/378425016651
 *  filter.p.m.sga.vehicle_brand_model → GID of sga-nest-vehicle_brand-model metaobject
 *                                        (NOT the sga-nest-vehicle_model handle)
 *
 * Metaobject field structure (confirmed via API inspection):
 *
 *  sga-nest-vehicle_model
 *    handle   → (not used as filter value)
 *    model    → string, e.g. "iX"  (model display name)
 *    znacka   → metaobject_reference → sga-nest-vehicle_brand
 *                 handle  → brand handle (used to match API brands)
 *                 fields  → first non-GID string value = brand display name
 *
 *  sga-nest-vehicle_brand-model
 *    id       → GID — used as filter.p.m.sga.vehicle_brand_model facet param value
 *    handle   → (used for dedup only)
 *    nazev    → string, e.g. "BMW iX"  (full display name for PDP option)
 *    znacka   → metaobject_reference → sga-nest-vehicle_brand
 *                 handle  → brand handle (used to group models by brand)
 *                 fields  → first non-GID string value = brand display name
 *    model    → metaobject_reference → sga-nest-vehicle_model  (not queried)
 *
 * Prerequisites:
 *  - `sga.storefrontApiToken` set in Theme Settings → Advanced.
 *  - `unauthenticated_read_metaobjects` scope on the token.
 *  - Storefront API access enabled on sga-nest-vehicle_model, sga-nest-vehicle_brand,
 *    and sga-nest-vehicle_brand-model metaobject definitions.
 */

(function () {
  const VEHICLE_MODEL_TYPE       = 'sga-nest-vehicle_model';
  const VEHICLE_BRAND_MODEL_TYPE = 'sga-nest-vehicle_brand-model';

  async function fetchAllMetaobjects(type, nodeFields) {
    const token = window.sga && window.sga.storefrontApiToken && window.sga.storefrontApiToken.trim();
    if (!token) return [];

    // Request translated field values in the page locale so that names returned
    // by the API match what Liquid renders (important for deduplication when
    // Translate & Adapt is active — e.g. "Jiné" in Czech vs "Other" in English).
    const pageLang = ((document.documentElement.lang || '').split('-')[0] || 'cs').toUpperCase();

    const endpoint = '/api/2026-01/graphql.json';
    const query = `
    query MetaobjectList($after: String) @inContext(language: ${pageLang}) {
      metaobjects(type: "${type}", first: 250, after: $after) {
        pageInfo { hasNextPage endCursor }
        nodes { ${nodeFields} }
      }
    }
  `;

    const nodes = [];
    let cursor = null;

    do {
      let response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query, variables: { after: cursor } }),
        });
      } catch (error) {
        console.error('[vehicle-selector] Storefront API request failed:', error);
        return null;
      }

      if (!response.ok) return null;

      let json;
      try {
        json = await response.json();
      } catch (error) {
        console.error('[vehicle-selector] Invalid Storefront API response (not JSON):', error);
        return null;
      }

      if (json.errors && json.errors.length) {
        console.error('[vehicle-selector] GraphQL error:', json.errors[0].message);
        return null;
      }

      if (!json.data) return null;

      const page = json.data.metaobjects;
      if (!page) return null;

      nodes.push(...page.nodes);
      cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
    } while (cursor);

    return nodes;
  }

  /** Returns the value for a named field from a `fields { key value }` array. */
  function getField(fields, key) {
    const f = fields && fields.find((f) => f.key === key);
    return f ? f.value : null;
  }

  /**
   * Returns the first field value that isn't a GID — used to extract the
   * human-readable display name from a brand metaobject's fields array.
   */
  function findDisplayName(fields) {
    if (!fields) return '';
    for (const f of fields) {
      if (f.value && !f.value.startsWith('gid://')) return f.value;
    }
    return '';
  }

  function escAttr(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Extracts the numeric tail of a Shopify GID string so that Storefront API
   * GIDs (e.g. "gid://shopify/Metaobject/378425016651") can be compared against
   * the numeric IDs that Liquid outputs via `{{ metaobject.id }}`.
   */
  function gidNumeric(gidString) {
    const match = gidString && gidString.match(/\/(\d+)$/);
    return match ? match[1] : (gidString || '');
  }

  let _vehicleModels   = null;
  let _brandModelItems = null;

  /**
   * Loads all sga-nest-vehicle_model objects with their brand reference resolved
   * inline. Used to extract the complete brand list for the collection filter.
   * Cached after first successful full load.
   */
  async function getVehicleModels() {
    if (_vehicleModels) return _vehicleModels;
    const vehicleModels = await fetchAllMetaobjects(
      VEHICLE_MODEL_TYPE,
      `handle
     fields { key value }
     brand: field(key: "znacka") {
       reference {
         ... on Metaobject {
           id
           handle
           fields { key value }
         }
       }
     }`
    );
    if (vehicleModels === null) return [];
    _vehicleModels = vehicleModels;
    return _vehicleModels;
  }

  /**
   * Loads all sga-nest-vehicle_brand-model objects with brand reference resolved
   * inline. Used for both the PDP selector and collection model list.
   * The `id` field provides the GID used as the facet filter value.
   * Cached after first successful full load.
   */
  async function getBrandModelItems() {
    if (_brandModelItems) return _brandModelItems;
    const brandModelItems = await fetchAllMetaobjects(
      VEHICLE_BRAND_MODEL_TYPE,
      `id
     handle
     fields { key value }
     brandRef: field(key: "znacka") {
       reference {
         ... on Metaobject {
           id
           handle
           fields { key value }
         }
       }
     }
     modelRef: field(key: "model") {
       reference {
         ... on Metaobject {
           handle
         }
       }
     }`
    );
    if (brandModelItems === null) return [];
    _brandModelItems = brandModelItems;
    return _brandModelItems;
  }

  /**
   * Fetches vehicle_brand_model metaobject references for a single product via
   * the Storefront API, paginated. Scoped to the product so we never load
   * unrelated vehicles from the store catalogue.
   *
   * Returns the same node shape as getBrandModelItems() (id, handle, fields,
   * modelRef) so the caller can reuse the same dedup + jine logic.
   */
  async function fetchProductVehicleReferences(productNumericId) {
    if (!productNumericId) return [];

    const token = window.sga && window.sga.storefrontApiToken && window.sga.storefrontApiToken.trim();
    if (!token) return [];

    const pageLang = ((document.documentElement.lang || '').split('-')[0] || 'cs').toUpperCase();
    const endpoint = '/api/2026-01/graphql.json';
    const productGid = `gid://shopify/Product/${productNumericId}`;

    const query = `
    query ProductVehicles($after: String) @inContext(language: ${pageLang}) {
      product(id: "${productGid}") {
        metafield(namespace: "sga", key: "vehicle_brand_model") {
          references(first: 250, after: $after) {
            nodes {
              ... on Metaobject {
                id
                handle
                fields { key value }
                modelRef: field(key: "model") {
                  reference {
                    ... on Metaobject { handle }
                  }
                }
              }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      }
    }`;

    const nodes = [];
    let cursor = null;

    do {
      let response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query, variables: { after: cursor } }),
        });
      } catch (error) {
        console.error('[vehicle-selector] Storefront API request failed:', error);
        return [];
      }

      if (!response.ok) return [];

      let json;
      try {
        json = await response.json();
      } catch (error) {
        console.error('[vehicle-selector] Invalid Storefront API response (not JSON):', error);
        return [];
      }

      if (json.errors && json.errors.length) {
        console.error('[vehicle-selector] GraphQL error:', json.errors[0].message);
        return [];
      }

      const page = json.data
        && json.data.product
        && json.data.product.metafield
        && json.data.product.metafield.references;
      if (!page) break;

      nodes.push(...page.nodes);
      cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
    } while (cursor);

    return nodes;
  }

  async function initProductSelectors() {
    const selects = document.querySelectorAll('[data-vehicle-selector]');
    if (!selects.length) return;

    for (const select of selects) {
      const currentValue = select.value;

      // Fetch all vehicle_brand_model entries for this product from the
      // Storefront API. This is product-scoped (not store-wide) so we only
      // load entries actually assigned to the product's metafield.
      // For products with ≤250 entries Liquid already rendered them all, so
      // the API results will deduplicate to nothing new — but the response
      // is still used for jine detection and to confirm completeness.
      const items = await fetchProductVehicleReferences(select.dataset.productId);

      // Append any entries that Liquid could not render (>250 metafield items).
      // Deduplicate by name, GID (with numeric normalisation), and metaobject
      // handle. The handle check is the language-agnostic fallback that catches
      // pairs like "Jiné" (Liquid, Czech) vs "Other" (API, English) — both share
      // the same handle even though their display names differ due to Translate & Adapt.
      const existingNames   = new Set();
      const existingGids    = new Set();
      const existingHandles = new Set();
      for (const option of Array.from(select.options).slice(1)) {
        existingNames.add(option.text);
        if (option.dataset.id)     existingGids.add(option.dataset.id);
        if (option.dataset.handle) existingHandles.add(option.dataset.handle);
      }

      for (const item of items) {
        const name = getField(item.fields, 'nazev');
        if (!name) continue;
        if (existingNames.has(name)) continue;
        if (item.id && (existingGids.has(item.id) || existingGids.has(gidNumeric(item.id)))) continue;
        if (item.handle && existingHandles.has(item.handle)) continue;
        const opt = new Option(name, name);
        if (item.id)     opt.dataset.id     = item.id;
        if (item.handle) opt.dataset.handle = item.handle;
        select.add(opt);
        existingNames.add(name);
        existingHandles.add(item.handle);
      }

      // Move all "Other / Jiné" entries (model handle: jine) to the end,
      // regardless of where they fell in the metafield or API order.
      // Liquid options are matched by data-handle; API-added options by data-id.
      const jineHandles      = new Set();
      const jineGids         = new Set();
      const jineOptionTexts  = new Set();
      for (const item of items) {
        const modelHandle = item.modelRef && item.modelRef.reference && item.modelRef.reference.handle;
        if (modelHandle === 'jine') {
          if (item.handle) jineHandles.add(item.handle);
          if (item.id)     jineGids.add(item.id);
          if (item.id)     jineGids.add(gidNumeric(item.id));
          // With @inContext the API returns names in the page locale, so this
          // text matches the Liquid-rendered option text for the same item.
          const jineName = getField(item.fields, 'nazev');
          if (jineName) jineOptionTexts.add(jineName);
        }
      }
      const toMove = (jineHandles.size || jineGids.size || jineOptionTexts.size)
        ? Array.from(select.options).filter((option) =>
            (option.dataset.handle && jineHandles.has(option.dataset.handle)) ||
            (option.dataset.id     && jineGids.has(option.dataset.id))         ||
            jineOptionTexts.has(option.text)
          )
        : [];
      for (const opt of toMove) {
        opt.remove();
        select.add(opt);
      }

      // Sort regular (non-jine) options alphabetically by display name.
      // Vehicle names follow "Brand Model" format, so localeCompare on the full
      // text naturally sorts by brand first, then by model within the brand.
      // jine options were already moved to the end of the select above.
      const jineOptSet = new Set(toMove);
      const regularOpts = Array.from(select.options)
        .slice(1)
        .filter((opt) => !jineOptSet.has(opt));
      regularOpts.sort((a, b) => a.text.localeCompare(b.text));
      while (select.options.length > 1) select.remove(1);
      for (const opt of regularOpts) select.add(opt);
      for (const opt of toMove) select.add(opt);

      if (currentValue) select.value = currentValue;
    }
  }

  /**
   * Builds a sorted, deduplicated array of brand objects `{ id, handle, name }`
   * from the resolved brand references on each vehicle model.
   */
  function extractBrands(models) {
    const seen = new Set();
    const brands = [];

    for (const v of models) {
      const ref = v.brand && v.brand.reference;
      if (!ref || !ref.handle) continue;

      if (!seen.has(ref.handle)) {
        seen.add(ref.handle);
        brands.push({
          id: ref.id,
          handle: ref.handle,
          name: findDisplayName(ref.fields) || ref.handle,
        });
      }
    }

    brands.sort((a, b) => a.name.localeCompare(b.name));

    // "Jiné" / "Other" (handle: jine) always sorts last
    const otherIdx = brands.findIndex((b) => b.handle === 'jine');
    if (otherIdx !== -1) brands.push(brands.splice(otherIdx, 1)[0]);

    return brands;
  }

  /**
   * Returns brand-model items for a given brand handle from the brand-model
   * dataset, sorted by model display name. Each entry exposes:
   *   - id:          GID of the sga-nest-vehicle_brand-model object → facet filter value
   *   - handle:      metaobject handle (used for dedup)
   *   - displayName: model name with brand prefix stripped, matching Liquid's display
   */
  function brandModelItemsForBrand(brandModelItems, brandHandle) {
    const items = [];

    for (const v of brandModelItems) {
      const ref = v.brandRef && v.brandRef.reference;
      if (!ref || ref.handle !== brandHandle) continue;

      const nazev = getField(v.fields, 'nazev') || '';
      const brandName = findDisplayName(ref.fields) || '';
      const prefix = brandName ? brandName + ' ' : '';
      const displayName = nazev.startsWith(prefix)
        ? nazev.slice(prefix.length).trim()
        : nazev.trim();
      if (!displayName || !v.id) continue;

      const modelHandle = v.modelRef && v.modelRef.reference && v.modelRef.reference.handle;
      items.push({ id: v.id, handle: v.handle, displayName, modelHandle: modelHandle || null });
    }

    items.sort((a, b) => a.displayName.localeCompare(b.displayName));

    // "Jiné" / "Other" (model handle: jine) always sorts last
    const otherIdx = items.findIndex((item) => item.modelHandle === 'jine');
    if (otherIdx !== -1) items.push(items.splice(otherIdx, 1)[0]);

    return items;
  }

  function buildBrandItem(brand, index, sectionId, activeBrandHandle) {
    const id = `VehicleBrandApi-${sectionId}-${index}`;
    const li = document.createElement('li');
    li.className = 'opacity-0 relative';
    if (brand.handle === 'jine') li.dataset.vehicleOther = 'true';
    li.innerHTML =
      `<input id="${escAttr(id)}" class="sr-only" ` +
      `name="vehicle_brand_filter_${escAttr(sectionId)}" type="radio" ` +
      `value="${escAttr(brand.id || brand.handle)}" data-vehicle-brand-input` +
      (brand.handle === activeBrandHandle ? ' checked' : '') +
      ` /><label for="${escAttr(id)}" class="reversed-link text-xl">${escAttr(brand.name)}</label>`;
    return li;
  }

  function buildModelItem(item, index, sectionId, activeModelValue) {
    const id = `VehicleModelApi-${sectionId}-${index}`;
    const li = document.createElement('li');
    li.className = 'opacity-0 relative';
    if (item.modelHandle === 'jine') li.dataset.vehicleOther = 'true';
    li.innerHTML =
      `<input id="${escAttr(id)}" class="sr-only" ` +
      `name="vehicle_model_filter_${escAttr(sectionId)}" type="radio" ` +
      `value="${escAttr(item.id)}" data-vehicle-model-input` +
      (item.id === activeModelValue ? ' checked' : '') +
      ` /><label for="${escAttr(id)}" class="reversed-link text-xl">${escAttr(item.displayName)}</label>`;
    return li;
  }

  function sortListboxItemsByLabel(listbox) {
    const firstItem = listbox.querySelector('li:first-child');
    const items = Array.from(listbox.querySelectorAll('li:not(:first-child)'));
    items.sort((a, b) => {
      const aOther = a.dataset.vehicleOther === 'true';
      const bOther = b.dataset.vehicleOther === 'true';
      if (aOther !== bOther) return aOther ? 1 : -1;
      const na = ((a.querySelector('label') || {}).textContent || '').trim();
      const nb = ((b.querySelector('label') || {}).textContent || '').trim();
      return na.localeCompare(nb);
    });
    listbox.innerHTML = '';
    if (firstItem) listbox.appendChild(firstItem);
    items.forEach((li) => listbox.appendChild(li));
  }

  /**
   * Adds any brands from `allBrands` that are not already present (matched by
   * normalised label text to avoid duplicates when Shopify's facet value differs
   * from the API handle), then re-sorts all brand items alphabetically.
   *
   * Liquid-rendered items keep their original `value` attribute so the Shopify
   * facet filter URL param continues to work correctly.
   */
  function augmentAndSortBrandListbox(brandListbox, allBrands, sectionId, activeBrandHandle) {
    // Index existing Liquid items by normalised label text AND by filter value (GID)
    const existingByName = new Map();
    const existingByGid  = new Map();
    for (const li of brandListbox.querySelectorAll('li:not(:first-child)')) {
      const label = li.querySelector('label');
      const input = li.querySelector('[data-vehicle-brand-input]');
      if (label) existingByName.set(label.textContent.trim().toLowerCase(), li);
      if (input && input.value) existingByGid.set(input.value, li);
    }

    // Mark the Liquid-rendered "Jiné" item (matched by GID) so the sorter can
    // push it last without relying on label text, which may differ by language.
    const otherBrand = allBrands.find((b) => b.handle === 'jine');
    if (otherBrand && otherBrand.id) {
      const otherLi = existingByGid.get(otherBrand.id);
      if (otherLi) otherLi.dataset.vehicleOther = 'true';
    }

    let idx = existingByName.size;
    for (const brand of allBrands) {
      // Skip if already represented by label OR by GID (prevents lang-mismatch dupes)
      if (existingByName.has(brand.name.toLowerCase())) continue;
      if (brand.id && existingByGid.has(brand.id)) continue;
      const li = buildBrandItem(brand, idx++, sectionId, activeBrandHandle);
      brandListbox.appendChild(li);
      existingByName.set(brand.name.toLowerCase(), li);
    }

    sortListboxItemsByLabel(brandListbox);
  }

  /**
   * Adds any models from the API dataset that are not already present in the
   * Liquid-rendered listbox (those beyond Shopify's facet value limit), then
   * re-sorts all model items alphabetically.
   *
   * API-added model items use the sga-nest-vehicle_brand-model GID as their
   * filter value, matching the format Shopify expects for this facet.
   * Liquid-rendered items keep their original `value` attribute.
   */
  function augmentAndSortModelListbox(modelListbox, brandModelItems, brandHandle, sectionId, activeModelValue) {
    const existingByName = new Map();
    const existingByGid  = new Map();
    for (const li of modelListbox.querySelectorAll('li:not(:first-child)')) {
      const label = li.querySelector('label');
      const input = li.querySelector('[data-vehicle-model-input]');
      if (label) existingByName.set(label.textContent.trim().toLowerCase(), li);
      if (input && input.value) existingByGid.set(input.value, li);
    }

    const apiItems = brandModelItemsForBrand(brandModelItems, brandHandle);

    // Mark any Liquid-rendered "Jiné" model item by GID match
    for (const item of apiItems) {
      if (item.modelHandle === 'jine' && item.id) {
        const otherLi = existingByGid.get(item.id);
        if (otherLi) otherLi.dataset.vehicleOther = 'true';
      }
    }

    let idx = existingByName.size;
    for (const item of apiItems) {
      if (existingByName.has(item.displayName.toLowerCase())) continue;
      if (item.id && existingByGid.has(item.id)) continue;
      const li = buildModelItem(item, idx++, sectionId, activeModelValue);
      modelListbox.appendChild(li);
      existingByName.set(item.displayName.toLowerCase(), li);
    }

    sortListboxItemsByLabel(modelListbox);
  }

  /**
   * Replaces the model listbox entirely with the full API model list for the
   * given brand. Used when there are no Liquid-rendered models to preserve
   * (e.g. after a brand change before the facet update fires).
   */
  function rebuildModelListbox(modelListbox, brandModelItems, brandHandle, sectionId, activeModelValue) {
    const allModelsItem = modelListbox.querySelector('li:first-child');
    modelListbox.innerHTML = '';
    if (allModelsItem) modelListbox.appendChild(allModelsItem);

    brandModelItemsForBrand(brandModelItems, brandHandle).forEach((item, i) => {
      modelListbox.appendChild(buildModelItem(item, i, sectionId, activeModelValue));
    });

    if (!activeModelValue) {
      const allModelsInput = modelListbox.querySelector('li:first-child input[data-vehicle-model-input]');
      if (allModelsInput) {
        allModelsInput.checked = true;
        modelListbox.querySelectorAll('input[data-vehicle-model-input]').forEach((el) => {
          if (el !== allModelsInput) el.checked = false;
        });
      }
    }
  }

  /**
   * Resolves a brand radio's filter value (Shopify facet GID or handle) to the
   * API brand handle used as the key in `brandModelItemsForBrand`. Matches by
   * handle, then brand metaobject id, then label text.
   */
  function resolveApiBrandHandle(inputValue, inputId, container, allBrands) {
    if (allBrands.find((b) => b.handle === inputValue)) return inputValue;
    const matchedById = allBrands.find((b) => b.id === inputValue);
    if (matchedById) return matchedById.handle;
    const label = container.querySelector(`label[for="${CSS.escape(inputId)}"]`);
    const labelText = label ? label.textContent.trim().toLowerCase() : '';
    const matched = allBrands.find((b) => b.name.toLowerCase() === labelText);
    return matched ? matched.handle : inputValue;
  }

  /**
   * Finds a brand-model item by GID, with a numeric-tail fallback so that
   * Storefront API GIDs match Liquid facet values even when one is a full
   * GID and the other is numeric-only.
   */
  function findBrandModelItem(brandModelItems, gid) {
    if (!gid) return null;
    const exact = brandModelItems.find((item) => item.id === gid);
    if (exact) return exact;
    const num = gidNumeric(gid);
    if (num !== gid) {
      return brandModelItems.find((item) => gidNumeric(item.id) === num) || null;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // Client-side model filtering (DOM-attribute based)
  // ---------------------------------------------------------------------------

  let _allPagesLoaded = false;
  let _originalProductCountHtml = null;
  let _savedFilterState = null;

  /**
   * Fetches all remaining paginated section pages and appends their product
   * cards to the grid. Uses the same section rendering URL pattern as the
   * theme's infinite-scroll pagination.
   */
  async function fetchAllBrandPages() {
    if (_allPagesLoaded) return;

    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const pagination = container.querySelector('.pagination');
    if (!pagination) {
      _allPagesLoaded = true;
      return;
    }

    const motionList = container.querySelector('motion-list');
    if (!motionList) return;

    const baseUrl = new URL(window.location.href);
    const form = document.querySelector('form[is="facet-form"]');
    const sectionId = form
      ? (window.theme && window.theme.utils && typeof window.theme.utils.sectionId === 'function'
          ? window.theme.utils.sectionId(form)
          : form.dataset.sectionId)
      : null;
    if (!sectionId) return;

    let page = 2;
    while (true) {
      const pageUrl = new URL(baseUrl.toString());
      pageUrl.searchParams.set('page', String(page));
      pageUrl.searchParams.set('section_id', sectionId);

      let html;
      try {
        const response = await fetch(pageUrl.toString());
        if (!response.ok) break;
        html = await response.text();
      } catch (_) {
        break;
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = parsed.querySelectorAll('motion-list .card');
      if (!cards.length) break;

      for (const card of cards) {
        motionList.appendChild(card);
      }

      if (!parsed.querySelector('.pagination')) break;
      page++;
    }

    pagination.style.display = 'none';
    _allPagesLoaded = true;
  }

  /**
   * Checks whether a product card's data-vehicle-models JSON array contains
   * the given brand-model GID (with numeric-tail fallback).
   */
  function cardMatchesModel(card, modelGid, numericGid) {
    const raw = card.getAttribute('data-vehicle-models');
    if (!raw) return false;
    let gids;
    try {
      gids = JSON.parse(raw);
    } catch (_) {
      return false;
    }
    if (!Array.isArray(gids)) return false;
    return gids.some((gid) => gid === modelGid || gidNumeric(gid) === numericGid);
  }

  /**
   * Loads all paginated pages, then hides product cards that do not match
   * the given brand-model GID using the data-vehicle-models DOM attribute.
   */
  async function filterByModel(modelGid) {
    await fetchAllBrandPages();

    const numericGid = gidNumeric(modelGid);
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const cards = container.querySelectorAll('.product-card');
    let visibleCount = 0;
    for (const card of cards) {
      if (cardMatchesModel(card, modelGid, numericGid)) {
        card.style.removeProperty('display');
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    }

    const countEl = document.getElementById('ProductCount');
    if (countEl) {
      if (_originalProductCountHtml === null) {
        _originalProductCountHtml = countEl.innerHTML;
      }
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = _originalProductCountHtml;
      const originalText = tempDiv.textContent.trim();
      const newText = originalText.replace(/\d+/, String(visibleCount));
      countEl.textContent = newText !== originalText ? newText : String(visibleCount);
    }

    syncNativeFilters();
  }

  function clearModelFilter() {
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      for (const card of container.querySelectorAll('.product-card')) {
        card.style.removeProperty('display');
      }
    }

    const countEl = document.getElementById('ProductCount');
    if (countEl && _originalProductCountHtml !== null) {
      countEl.innerHTML = _originalProductCountHtml;
    }
    _originalProductCountHtml = null;

    restoreNativeFilters();
  }

  // ---------------------------------------------------------------------------
  // Native filter synchronization
  // ---------------------------------------------------------------------------

  function collectVisibleProductData() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return [];

    const data = [];
    for (const card of container.querySelectorAll('.product-card')) {
      if (card.style.display === 'none') continue;
      const raw = card.getAttribute('data-product-filters');
      if (!raw) continue;
      try {
        data.push(JSON.parse(raw));
      } catch (_) {
        // skip malformed JSON
      }
    }
    return data;
  }

  function buildFilterCounts(products) {
    const counts = {
      tags: {},
      type: {},
      vendor: {},
      available: 0,
      priceMin: Infinity,
      priceMax: -Infinity,
      options: {},
      mf: {},
    };

    for (const p of products) {
      if (Array.isArray(p.tags)) {
        for (const tag of p.tags) {
          counts.tags[tag] = (counts.tags[tag] || 0) + 1;
        }
      }
      if (p.type) counts.type[p.type] = (counts.type[p.type] || 0) + 1;
      if (p.vendor) counts.vendor[p.vendor] = (counts.vendor[p.vendor] || 0) + 1;
      if (p.available) counts.available++;
      if (typeof p.price === 'number') {
        if (p.price < counts.priceMin) counts.priceMin = p.price;
        if (p.price > counts.priceMax) counts.priceMax = p.price;
      }

      if (p.options && typeof p.options === 'object') {
        for (const [optName, optValues] of Object.entries(p.options)) {
          const key = optName.toLowerCase();
          if (!counts.options[key]) counts.options[key] = {};
          if (Array.isArray(optValues)) {
            for (const v of optValues) {
              counts.options[key][v] = (counts.options[key][v] || 0) + 1;
            }
          }
        }
      }

      if (p.mf && typeof p.mf === 'object') {
        for (const [key, val] of Object.entries(p.mf)) {
          if (val === '' || val == null) continue;
          if (!counts.mf[key]) counts.mf[key] = {};
          const vals = Array.isArray(val) ? val : [val];
          for (const v of vals) {
            if (v === '' || v == null) continue;
            const strVal = String(v);
            counts.mf[key][strVal] = (counts.mf[key][strVal] || 0) + 1;
          }
        }
      }
    }

    return counts;
  }

  function getCountForInput(input, counts) {
    const name = input.getAttribute('name') || '';
    const value = input.value || '';

    if (name.startsWith('filter.p.tag')) {
      return counts.tags[value] || 0;
    }
    if (name.startsWith('filter.p.product_type')) {
      return counts.type[value] || 0;
    }
    if (name.startsWith('filter.p.vendor')) {
      return counts.vendor[value] || 0;
    }
    if (name === 'filter.v.availability') {
      return counts.available;
    }
    if (name.startsWith('filter.v.option.')) {
      const optName = name.replace('filter.v.option.', '').toLowerCase();
      const optMap = counts.options[optName];
      return optMap ? (optMap[value] || 0) : 0;
    }
    if (name.startsWith('filter.p.m.') || name.startsWith('filter.v.m.')) {
      const mfKey = name.replace(/^filter\.[pv]\.m\./, '');
      const mfMap = counts.mf[mfKey];
      return mfMap ? (mfMap[value] || 0) : 0;
    }

    return -1;
  }

  function saveFilterState() {
    if (_savedFilterState) return;

    _savedFilterState = [];
    const forms = document.querySelectorAll('form.facet-form');
    for (const form of forms) {
      for (const input of form.querySelectorAll('input[type="checkbox"]')) {
        const label = form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        _savedFilterState.push({
          input,
          label,
          disabled: input.disabled,
          countHtml: label ? label.innerHTML : null,
        });
      }
    }
  }

  function restoreNativeFilters() {
    if (!_savedFilterState) return;

    for (const entry of _savedFilterState) {
      entry.input.disabled = entry.disabled;
      if (entry.label && entry.countHtml !== null) {
        entry.label.innerHTML = entry.countHtml;
      }
    }
    _savedFilterState = null;
  }

  function syncNativeFilters() {
    const products = collectVisibleProductData();
    if (!products.length) return;

    saveFilterState();

    const counts = buildFilterCounts(products);
    const forms = document.querySelectorAll('form.facet-form');

    for (const form of forms) {
      for (const input of form.querySelectorAll('input[type="checkbox"]')) {
        const count = getCountForInput(input, counts);
        if (count === -1) continue;

        if (count === 0 && !input.checked) {
          input.disabled = true;
        } else {
          input.disabled = false;
        }

        const label = form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) continue;

        const countSpan = label.querySelector('span.text-sm');
        if (countSpan) {
          countSpan.textContent = `(${count})`;
          continue;
        }

        const tooltip = label.querySelector('.tooltip');
        if (tooltip) {
          tooltip.textContent = tooltip.textContent.replace(/\(\d+\)/, `(${count})`);
          continue;
        }

        const badge = label.querySelector('span.count');
        if (badge) {
          badge.textContent = String(count);
        }
      }
    }
  }

  /**
   * Programmatically checks the model radio and updates the sort-selection
   * label for a given brand-model GID. Used after a pending model filter
   * is applied following a brand-only re-render.
   */
  function activateModelRadioAndLabel(filterEl, modelGid, brandModelItems) {
    const modelListbox = filterEl.querySelector('.vehicle-filter__sort--model .sort-listbox');
    if (!modelListbox) return;

    let modelInput = modelListbox.querySelector(
      `[data-vehicle-model-input][value="${CSS.escape(modelGid)}"]`
    );
    if (!modelInput) {
      const numericModel = gidNumeric(modelGid);
      for (const input of modelListbox.querySelectorAll('[data-vehicle-model-input]')) {
        if (input.value && gidNumeric(input.value) === numericModel) {
          modelInput = input;
          break;
        }
      }
    }
    if (modelInput) {
      modelInput.checked = true;
      for (const el of modelListbox.querySelectorAll('[data-vehicle-model-input]')) {
        if (el !== modelInput) el.checked = false;
      }
    }

    const matchedItem = findBrandModelItem(brandModelItems, modelGid);
    if (matchedItem) {
      const modelSortEl = filterEl.querySelector('.vehicle-filter__sort--model');
      if (modelSortEl) {
        const nazev = getField(matchedItem.fields, 'nazev') || '';
        const brandRef = matchedItem.brandRef && matchedItem.brandRef.reference;
        const brandName = brandRef ? findDisplayName(brandRef.fields) : '';
        const prefix = brandName ? brandName + ' ' : '';
        const displayName = nazev.startsWith(prefix)
          ? nazev.slice(prefix.length).trim()
          : nazev.trim();
        const selection = modelSortEl.querySelector('.sort-selection');
        if (selection) selection.textContent = displayName || nazev;
        if (typeof modelSortEl.initButton === 'function') modelSortEl.initButton();
      }
    }
  }

  function applyToVehicleFilter(filterEl, models, brandModelItems) {
    const sectionId    = filterEl.dataset.sectionId || '';
    const brandListbox = filterEl.querySelector('.vehicle-filter__sort--brand .sort-listbox');
    const modelListbox = filterEl.querySelector('.vehicle-filter__sort--model .sort-listbox');
    if (!brandListbox) return;

    const allBrands = extractBrands(models);

    if (!allBrands.length) {
      console.warn('[vehicle-selector] No brand references resolved — ensure Storefront API access is enabled on the sga-nest-vehicle_brand metaobject type.');
    }

    const url = new URL(window.location.href);
    const activeBrandModelParam = url.searchParams.get('filter.p.m.sga.vehicle_brand_model');
    const activeBrandParam      = url.searchParams.get('filter.p.m.sga.vehicle_brand');

    const activeBrandInput = brandListbox.querySelector('[data-vehicle-brand-input]:checked');
    const activeModelInput = modelListbox && modelListbox.querySelector('[data-vehicle-model-input]:checked');
    const activeModelValue = (activeModelInput && activeModelInput.value) || '';

    let apiBrandHandle    = '';
    let inferredBrandGid  = null;
    let inferredBrandName = '';
    let inferredModelName = '';

    if (activeBrandInput && activeBrandInput.value) {
      apiBrandHandle = resolveApiBrandHandle(
        activeBrandInput.value,
        activeBrandInput.id,
        brandListbox,
        allBrands
      );
    } else if (activeBrandModelParam) {
      // No brand radio checked but a brand-model filter is active in the URL.
      // This happens when Liquid's prefix inference fails (the model for-loop
      // is gated behind has_active_brand) — no model radios are rendered so we
      // cannot rely on checked radios. Read directly from the URL and the API.
      const matchedItem = findBrandModelItem(brandModelItems, activeBrandModelParam);
      const brandRef = matchedItem && matchedItem.brandRef && matchedItem.brandRef.reference;
      if (brandRef && brandRef.handle) {
        apiBrandHandle   = brandRef.handle;
        inferredBrandGid = brandRef.id || null;
        inferredBrandName = findDisplayName(brandRef.fields) || brandRef.handle;

        const nazev     = getField(matchedItem.fields, 'nazev') || '';
        const prefix    = inferredBrandName ? inferredBrandName + ' ' : '';
        inferredModelName = nazev.startsWith(prefix)
          ? nazev.slice(prefix.length).trim()
          : nazev.trim();
      }
    }

    augmentAndSortBrandListbox(brandListbox, allBrands, sectionId, apiBrandHandle);

    if (inferredBrandGid) {
      // Programmatically check the brand radio so :checked CSS reflects the
      // selected state. Try the full GID first, then fall back to numeric match.
      let brandInputToCheck = brandListbox.querySelector(
        `[data-vehicle-brand-input][value="${CSS.escape(inferredBrandGid)}"]`
      );
      if (!brandInputToCheck) {
        const numericGid = gidNumeric(inferredBrandGid);
        brandListbox.querySelectorAll('[data-vehicle-brand-input]').forEach((input) => {
          if (!brandInputToCheck && input.value && gidNumeric(input.value) === numericGid) {
            brandInputToCheck = input;
          }
        });
      }
      if (brandInputToCheck) brandInputToCheck.checked = true;
    }

    const shareableModelGid = url.searchParams.get('vehicle_model');
    const effectiveActiveModel = activeModelValue || activeBrandModelParam || shareableModelGid || '';

    if (apiBrandHandle && modelListbox) {
      const hasLiquidModels = modelListbox.querySelectorAll(
        'li:not(:first-child) [data-vehicle-model-input]'
      ).length > 0;

      if (hasLiquidModels) {
        augmentAndSortModelListbox(modelListbox, brandModelItems, apiBrandHandle, sectionId, effectiveActiveModel);
      } else {
        rebuildModelListbox(modelListbox, brandModelItems, apiBrandHandle, sectionId, effectiveActiveModel);
      }

      // Ensure the correct model radio is checked — the active value might be
      // from the URL param rather than from Liquid's checked attribute.
      if (activeBrandModelParam && !activeModelValue) {
        let modelInputToCheck = modelListbox.querySelector(
          `[data-vehicle-model-input][value="${CSS.escape(activeBrandModelParam)}"]`
        );
        if (!modelInputToCheck) {
          const numericModel = gidNumeric(activeBrandModelParam);
          modelListbox.querySelectorAll('[data-vehicle-model-input]').forEach((input) => {
            if (!modelInputToCheck && input.value && gidNumeric(input.value) === numericModel) {
              modelInputToCheck = input;
            }
          });
        }
        if (modelInputToCheck) {
          modelInputToCheck.checked = true;
          modelListbox.querySelectorAll('[data-vehicle-model-input]').forEach((el) => {
            if (el !== modelInputToCheck) el.checked = false;
          });
        }
      }
    }

    // When brand/model were inferred from the URL (Liquid inference failed),
    // update the visible .sort-selection labels and remove disabled state from
    // the model control so the UI reflects the actual active filters.
    if (inferredBrandGid) {
      const brandSortEl = filterEl.querySelector('.vehicle-filter__sort--brand');
      if (brandSortEl && inferredBrandName) {
        const brandSelection = brandSortEl.querySelector('.sort-selection');
        if (brandSelection) brandSelection.textContent = inferredBrandName;
        if (typeof brandSortEl.initButton === 'function') brandSortEl.initButton();
      }

      const modelSortEl = filterEl.querySelector('.vehicle-filter__sort--model');
      if (modelSortEl) {
        if (inferredModelName) {
          const modelSelection = modelSortEl.querySelector('.sort-selection');
          if (modelSelection) modelSelection.textContent = inferredModelName;
        }

        modelSortEl.classList.remove('is-disabled');
        const modelHoverEl = modelSortEl.querySelector('.sort-by');
        if (modelHoverEl) {
          modelHoverEl.classList.remove('pointer-events-none', 'opacity-60');
          modelHoverEl.removeAttribute('aria-disabled');
        }

        if (typeof modelSortEl.initButton === 'function') modelSortEl.initButton();
      }
    }

    // Pending model filter: set by vehicle-filter.js when the user selects a
    // model while a server-side brand_model param is in the URL. The brand-only
    // re-render has now completed — apply the client-side model filter.
    const pendingModelGid = filterEl.dataset.pendingModelGid;
    if (pendingModelGid) {
      delete filterEl.dataset.pendingModelGid;
      activateModelRadioAndLabel(filterEl, pendingModelGid, brandModelItems);
      filterByModel(pendingModelGid);
      return;
    }

    if (shareableModelGid && activeBrandParam && !filterEl.dataset.shareableLinkHandled) {
      filterEl.dataset.shareableLinkHandled = 'true';
      filterEl.dataset.clientSideFilter = 'active';
      filterEl.dataset.activeModelGid = shareableModelGid;

      const modelSortEl = filterEl.querySelector('.vehicle-filter__sort--model');
      if (modelSortEl) {
        modelSortEl.classList.remove('is-disabled');
        const hoverEl = modelSortEl.querySelector('.sort-by');
        if (hoverEl) {
          hoverEl.classList.remove('pointer-events-none', 'opacity-60');
          hoverEl.removeAttribute('aria-disabled');
        }
      }

      activateModelRadioAndLabel(filterEl, shareableModelGid, brandModelItems);
      filterByModel(shareableModelGid);
      return;
    }

    // Re-apply active client-side model filter after other facets (price, sort,
    // etc.) trigger a server re-render that replaces the grid and innerHTML.
    const activeModelGid = filterEl.dataset.activeModelGid;
    if (filterEl.dataset.clientSideFilter === 'active' && activeModelGid) {
      activateModelRadioAndLabel(filterEl, activeModelGid, brandModelItems);
      filterByModel(activeModelGid);
    }
  }

  /**
   * Watches for innerHTML replacements by vehicle-filter.js `onFacetUpdate` and
   * re-applies the full dataset after each swap.
   */
  function observeVehicleFilter(filterEl, models, brandModelItems) {
    applyToVehicleFilter(filterEl, models, brandModelItems);

    const observer = new MutationObserver(() => {
      observer.disconnect();
      try {
        applyToVehicleFilter(filterEl, models, brandModelItems);
      } finally {
        observer.observe(filterEl, { childList: true });
      }
    });

    observer.observe(filterEl, { childList: true });
  }

  async function initVehicleFilters() {
    const filters = document.querySelectorAll('vehicle-filter');
    if (!filters.length) return;

    const [models, brandModelItems] = await Promise.all([getVehicleModels(), getBrandModelItems()]);
    if (!models.length) return;

    for (const filterEl of filters) {
      observeVehicleFilter(filterEl, models, brandModelItems);
    }

    const themeApi = window.theme;
    if (themeApi && themeApi.pubsub) {
      themeApi.pubsub.subscribe('vehicle:modelFilter', (event) => {
        if (event.modelGid) {
          filterByModel(event.modelGid);
        } else {
          clearModelFilter();
        }
      });

      themeApi.pubsub.subscribe(themeApi.pubsub.PUB_SUB_EVENTS.facetUpdate, () => {
        _allPagesLoaded = false;
        _originalProductCountHtml = null;
        _savedFilterState = null;
      });
    }
  }

  /**
   * Populates every `[data-vehicle-list]` element with the full, alphabetically
   * sorted vehicle list for the current product. Reads directly from the
   * `[data-vehicle-selector]` select element, which is already populated with
   * the complete dataset by `initProductSelectors()` — no additional API call.
   */
  function initVehicleLists() {
    const lists = document.querySelectorAll('[data-vehicle-list]');
    if (!lists.length) return;

    const selectEl = document.querySelector('[data-vehicle-selector]');
    if (!selectEl) return;

    const vehicleNames = Array.from(selectEl.options)
      .slice(1)
      .map((option) => option.text.trim())
      .filter(Boolean);

    for (const listEl of lists) {
      listEl.innerHTML = '';
      for (const vehicleName of vehicleNames) {
        const listItem = document.createElement('li');
        listItem.textContent = vehicleName;
        listEl.appendChild(listItem);
      }
    }
  }

  async function init() {
    await initProductSelectors();
    initVehicleFilters();
    initVehicleLists();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
